import { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, Search, Filter, ClipboardList, ChevronDown, X } from 'lucide-react';
import { lmsAPI } from '../services/api';
import { downloadCsvLegacy } from '../utils/download';
import { useAuthStore } from '../stores/authStore';
import ScormProgressReportPage from './ScormProgressReportPage';
import { useTableSort, SortTh } from '../components/dashboard/useTableSort';
import { BlockLoader } from '../components/loading';

type QuizStatus = 'not_started' | 'in_progress' | 'completed' | 'failed';

interface QuizRow {
  employee_code: string;
  employee_name: string;
  module_id: string;
  module_title: string;
  pass_score: number;
  attempts: number;
  latest_score: number | null;
  completed_at: string | null;
  started_at: string | null;
  status: QuizStatus;
}

interface ModuleOption {
  id: string;
  title: string;
  status: string;
}

const statusConfig: Record<QuizStatus, { label: string; bg: string; fg: string }> = {
  not_started: { label: 'Not Started', bg: 'bg-gray-100', fg: 'text-gray-700' },
  in_progress: { label: 'In Progress', bg: 'bg-yellow-100', fg: 'text-yellow-700' },
  completed: { label: 'Passed', bg: 'bg-green-100', fg: 'text-green-700' },
  failed: { label: 'Failed', bg: 'bg-red-100', fg: 'text-red-700' },
};

// Default to last 30 days — quiz completions trickle in over weeks, so a
// 30-day window is the right landing view. Yesterday-only (the attendance
// convention) would render an almost-empty table on most days.
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Epoch ms, so date columns sort chronologically (null → sinks to the bottom).
const toMs = (iso: string | null): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
};

export default function QuizPerformanceReportPage() {
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);
  const projectId = selectedProjectId ?? undefined;

  // Native quiz modules and imported SCORM courses report completion through different
  // pipelines, so they get their own tab rather than being merged into one table with
  // half the columns blank on each row.
  const [view, setView] = useState<'quiz' | 'scorm'>('quiz');
  const [rows, setRows] = useState<QuizRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(today());
  const [statusFilter, setStatusFilter] = useState<QuizStatus | 'all'>('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedModuleIds, setSelectedModuleIds] = useState<string[]>([]);
  const [showModulePicker, setShowModulePicker] = useState(false);
  const [showStatusPicker, setShowStatusPicker] = useState(false);

  // Modules for the multi-select
  const [modules, setModules] = useState<ModuleOption[]>([]);

  // Debounce employee search so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Load module list once for the multi-select. Only published modules —
  // unpublished ones aren't assignable, so they can't appear in the report.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await lmsAPI.getContentModules({ status: 'published', projectId });
        if (cancelled) return;
        const data = resp.data?.data || [];
        setModules(data.map((m: any) => ({ id: String(m.id), title: m.title, status: m.status })));
      } catch (err) {
        console.warn('Could not load module list:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const fetchReport = useCallback(async () => {
    if (!projectId || view !== 'quiz') {
      setRows([]);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const resp = await lmsAPI.getQuizPerformanceReport({
        fromDate: startDate || undefined,
        toDate: endDate || undefined,
        moduleIds: selectedModuleIds.length > 0 ? selectedModuleIds : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        q: searchQuery || undefined,
        projectId,
      });
      const data = resp.data?.data || [];
      setRows(data.map((r: any) => ({
        employee_code: r.employee_code,
        employee_name: r.employee_name || r.employee_code,
        module_id: r.module_id,
        module_title: r.module_title,
        pass_score: parseInt(r.pass_score) || 0,
        attempts: parseInt(r.attempts) || 0,
        latest_score: r.latest_score !== null && r.latest_score !== undefined ? parseInt(r.latest_score) : null,
        completed_at: r.completed_at,
        started_at: r.started_at,
        status: (r.status || 'not_started') as QuizStatus,
      })));
    } catch (err) {
      console.error('Error loading quiz performance report:', err);
      setError('Failed to load quiz performance report');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, view, startDate, endDate, selectedModuleIds, statusFilter, searchQuery]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Mirrors STATUSES_WITHOUT_COMPLETION in lms.controller.ts.
  const dateRangeIgnored = statusFilter === 'not_started' || statusFilter === 'in_progress';

  const moduleLabel = useMemo(() => {
    if (selectedModuleIds.length === 0) return 'All Modules';
    if (selectedModuleIds.length === 1) {
      const m = modules.find((mm) => mm.id === selectedModuleIds[0]);
      return m?.title ?? '1 module';
    }
    return `${selectedModuleIds.length} modules`;
  }, [selectedModuleIds, modules]);

  const sort = useTableSort<QuizRow>(rows, {
    // An unresolved name falls back to the code and renders '—' — treat it as missing.
    name: (r) => (r.employee_name === r.employee_code ? null : r.employee_name),
    code: (r) => r.employee_code,
    module: (r) => r.module_title,
    attempts: (r) => r.attempts,
    score: (r) => r.latest_score,
    passPct: (r) => r.pass_score,
    status: (r) => statusConfig[r.status].label,
    completed: (r) => toMs(r.completed_at),
  });

  const handleExportCSV = () => {
    if (rows.length === 0) return;
    const header = [
      'Employee Code',
      'Employee Name',
      'Module',
      'Attempts',
      'Latest Score (%)',
      'Pass Score (%)',
      'Status',
      'Started At',
      'Completed At',
    ];
    const lines = rows.map((r) => [
      r.employee_code,
      r.employee_name,
      r.module_title,
      String(r.attempts),
      r.latest_score !== null ? String(r.latest_score) : '',
      String(r.pass_score),
      statusConfig[r.status].label,
      r.started_at ? new Date(r.started_at).toISOString() : '',
      r.completed_at ? new Date(r.completed_at).toISOString() : '',
    ]);
    const csv = [header, ...lines]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    downloadCsvLegacy(`quiz-performance-${startDate}-to-${endDate}.csv`, csv);
  };

  const toggleModule = (id: string) => {
    setSelectedModuleIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <ClipboardList className="h-7 w-7 text-primary-600" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Quiz Performance</h1>
            <p className="text-sm text-gray-500 mt-1">
              Module-by-module quiz outcomes for assigned employees (direct + role-based).
            </p>
          </div>
        </div>
        {view === 'quiz' && (
          <button
            onClick={handleExportCSV}
            disabled={rows.length === 0 || loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {([['quiz', 'Quiz Modules'], ['scorm', 'SCORM Courses']] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setView(key)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${
              view === key
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {view === 'scorm' && <ScormProgressReportPage embedded />}

      {view === 'quiz' && (
      <section className="rounded-lg border border-gray-100 bg-white p-6">
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <div className="grid grid-cols-2 gap-2">
            {/* Not Started / In Progress rows have no completion date, so the
                API ignores the range for them. Say so rather than leaving the
                inputs looking active but inert. */}
            {dateRangeIgnored && (
              <p className="col-span-2 -mb-1 text-xs text-amber-600">
                Date range doesn't apply to {statusConfig[statusFilter as QuizStatus].label} — those quizzes have no completion date.
              </p>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Module multi-select */}
          <div className="relative">
            <label className="block text-xs font-medium text-gray-600 mb-1">Module</label>
            <button
              type="button"
              onClick={() => {
                setShowModulePicker((v) => !v);
                setShowStatusPicker(false);
              }}
              className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
            >
              <span className="truncate">{moduleLabel}</span>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </button>
            {showModulePicker && (
              <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-2">
                <div className="flex items-center justify-between px-2 py-1">
                  <button
                    onClick={() => setSelectedModuleIds([])}
                    className="text-xs text-primary-600 hover:underline"
                    type="button"
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => setShowModulePicker(false)}
                    className="text-xs text-gray-500 hover:text-gray-700"
                    type="button"
                  >
                    Close
                  </button>
                </div>
                {modules.length === 0 && (
                  <p className="px-3 py-2 text-sm text-gray-500">No published modules</p>
                )}
                {modules.map((m) => (
                  <label
                    key={m.id}
                    className="flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedModuleIds.includes(m.id)}
                      onChange={() => toggleModule(m.id)}
                      className="w-4 h-4"
                    />
                    <span className="truncate">{m.title}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Status */}
          <div className="relative">
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <button
              type="button"
              onClick={() => {
                setShowStatusPicker((v) => !v);
                setShowModulePicker(false);
              }}
              className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
            >
              <span className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                {statusFilter === 'all' ? 'All Status' : statusConfig[statusFilter].label}
              </span>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </button>
            {showStatusPicker && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                {(['all', 'completed', 'failed', 'in_progress', 'not_started'] as const).map((s) => (
                  <button
                    type="button"
                    key={s}
                    onClick={() => {
                      setStatusFilter(s);
                      setShowStatusPicker(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                  >
                    {s === 'all' ? 'All Status' : statusConfig[s].label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Employee search */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Employee</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by name or code…"
                className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                  type="button"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <BlockLoader label="Loading…" className="py-16 text-center" />
        ) : error ? (
          <div className="py-16 text-center text-sm text-red-600">{error}</div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center">
            <ClipboardList className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="text-sm text-gray-500">No quiz performance data for this filter.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/70 border-b border-gray-100">
                  <SortTh label="Employee Name" sortKey="name" sort={sort} className="px-4 py-3 text-left text-xs font-semibold text-gray-600" />
                  <SortTh label="Employee Code" sortKey="code" sort={sort} className="px-4 py-3 text-left text-xs font-semibold text-gray-600" />
                  <SortTh label="Module" sortKey="module" sort={sort} className="px-4 py-3 text-left text-xs font-semibold text-gray-600" />
                  <SortTh label="Attempts" sortKey="attempts" sort={sort} className="px-4 py-3 text-left text-xs font-semibold text-gray-600" />
                  <SortTh label="Score" sortKey="score" sort={sort} className="px-4 py-3 text-left text-xs font-semibold text-gray-600" />
                  <SortTh label="Pass %" sortKey="passPct" sort={sort} className="px-4 py-3 text-left text-xs font-semibold text-gray-600" />
                  <SortTh label="Status" sortKey="status" sort={sort} className="px-4 py-3 text-left text-xs font-semibold text-gray-600" />
                  <SortTh label="Completed" sortKey="completed" sort={sort} className="px-4 py-3 text-left text-xs font-semibold text-gray-600" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sort.sorted.map((r, i) => {
                  const sc = statusConfig[r.status];
                  return (
                    <tr key={`${r.employee_code}|${r.module_id}|${i}`} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {/* Falls back to the code when neither 1Office nor
                            external_users can resolve a name for it. */}
                        {r.employee_name === r.employee_code
                          ? <span className="text-gray-400">—</span>
                          : r.employee_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{r.employee_code}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{r.module_title}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{r.attempts}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {r.latest_score !== null ? `${r.latest_score}%` : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{r.pass_score}%</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${sc.bg} ${sc.fg}`}>
                          {sc.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{formatDate(r.completed_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-xs text-gray-400 mt-3">
              {rows.length} row{rows.length === 1 ? '' : 's'}{rows.length >= 5000 ? ' (capped at 5000 — narrow your filters)' : ''}
            </p>
          </div>
        )}
      </section>
      )}

      {/* Click outside to close dropdowns */}
      {(showModulePicker || showStatusPicker) && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => {
            setShowModulePicker(false);
            setShowStatusPicker(false);
          }}
        />
      )}
    </div>
  );
}
